import jsPDF from 'jspdf';
import { toast } from 'react-toastify';
import { deleteCertificateImage, deleteCertificateInfo, uploadCertificateImage } from '../firebase/utils';
import { dynamicImage } from '../Services';

export const handleDownloadCertificate = async (certificates, email) => {
    if (!certificates.length) {
        toast.error("No certificates to download");
        return;
    }

    toast.success(`Downloading certificate${certificates.length > 1 ? 's' : ''}...`);

    for (const cert of certificates) {
        if (!cert?.url) continue;

        try {
            const response = await fetch(cert.url);
            const blob = await response.blob();

            const imageData = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onloadend = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });

            const pdf = new jsPDF({
                orientation: 'landscape',
                unit: 'px',
                format: [2000, 1414],
            });

            pdf.addImage(imageData, 'PNG', 0, 0, 2000, 1414);
            const studentEmail = email || cert.email || 'student';
            pdf.save(`${studentEmail}-certificate.pdf`);

        } catch (error) {
            console.error(`Failed to download certificate for ${cert?.email || email}:`, error);
            toast.error(`Failed to download certificate for ${cert?.email || email}`);
        }
    }
};

export const handleGenerateCertificate = (fullName, studentId, courseName, formattedDate, courseCategory = "Web & Mobile App Development") => {
    return new Promise((resolve, reject) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        const width = 2000;
        const height = 1414;
        canvas.width = width;
        canvas.height = height;

        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.src = dynamicImage('Certificate_Template_Final.png');

        img.onload = async () => {
            try {
                await document.fonts.load('65px Raleway');
                await document.fonts.load('32px Montserrat');

                ctx.drawImage(img, 0, 0, width, height);

                ctx.fillStyle = '#2e9fcd';
                ctx.font = 'bold 65px Raleway';
                ctx.fillText(fullName.toUpperCase(), 150, 615);

                ctx.fillStyle = '#090909';
                ctx.font = '32px Montserrat';
                ctx.fillText(courseName, 429, 667);
                ctx.fillText(`${courseCategory} Course`, 400, 806);
                ctx.fillText(formattedDate, 320, 876);

                const dataURL = canvas.toDataURL('image/png');
                const imageUrl = await uploadCertificateImage(dataURL, courseName, studentId);
                resolve(imageUrl);
            } catch (err) {
                console.error("Error during certificate generation:", err);
                toast.error("Certificate generation failed");
                reject(err);
            }
        };

        img.onerror = (err) => {
            console.error("Image load error:", err);
            toast.error("Failed to load certificate template");
            reject("Image load error");
        };
    });
};

export const updateStudentCourses = (students, { targetStudentId, updatedCourses }) => {
    return students.map((student) => {
        if (student.id !== targetStudentId) return student;

        return {
            ...student,
            courses: updatedCourses,
        };
    });
};

export const hasGeneratedCertificate = (student, targetCourseId) => {
    return student?.courses?.some(course => {
        const { certificate = {}, courseId } = course;
        return courseId === targetCourseId && !!certificate.url;
    }) || false;
};

export const getCertificateForCourse = (student, courseId) => {
    return student?.courses?.find(course => course?.courseId === courseId)?.certificate;
};

export const removeCertificateCompletely = async ({ studentId, courseId, courseName }) => {
    const imageDeleted = await deleteCertificateImage(courseName, studentId);

    if (imageDeleted) toast.success(`Certificate deleted successfully for ${studentId}`);
    else toast.error(`Failed to delete certificate for ${studentId}`);

    const certInfoDeleted = await deleteCertificateInfo(studentId, courseId);

    if (!certInfoDeleted?.length) toast.error(`Failed to delete certificate info for ${studentId}`);

    return certInfoDeleted;
}

export const handleSummaryFeedback = (total, { success, failed, skipped }) => {
    if (skipped.length === total) {
        toast.error("Certificates already generated for all selected students.");
        return;
    }

    if (failed.length === total) {
        toast.error("Failed to generate certificates for all selected students.");
        return;
    }

    if (success.length === total) {
        toast.success("Certificates generated for all selected students.");
        return;
    }

    if (success.length > 0) {
        toast.success(`Certificates generated for ${success.length} student(s).`);
    }

    if (skipped.length > 0) {
        toast.info(`${skipped.length} student(s) were skipped (already generated).`);
    }

    if (failed.length > 0) {
        toast.error(`${failed.length} student(s) failed during certificate generation.`);
    }
};